import { Timestamp, collection, doc, serverTimestamp } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { STORAGE_URL, firestore, storage } from 'services';
import { Story } from 'types';
import { StoryFieldsType } from 'validations/story';

const ValidFileState = ['added', 'exists'];

const getStoryStatus = (publish: boolean, hasAvilableDateRange: boolean): Story['status'] => {
  if (!publish) return 'draft';
  if (publish && !hasAvilableDateRange) return 'published';
  if (publish && hasAvilableDateRange) return 'scheduled';

  return 'unpublished';
};

// TODO Uncomment once the sizes are decided
// TASK_ID: #8694x2n39
//
// const resizeAndGetBuffer = async (file: File, width: number, height: number, type: ImageFormat) => {
//   const fileUrl = fileToObjectUrl(file);
//   return await resizeImage(fileUrl, width, height);
// };

export const prepareStoryForUpdate = async (
  formData: StoryFieldsType,
  publish: boolean,
  files: { [key: string]: File },
  id?: string,
) => {
  const errors = [];
  if (!formData.imageUrl?.state || !ValidFileState.includes(formData.imageUrl?.state)) {
    errors.push(`Story image is missing!`);
  }

  if (!formData.titleImageUrl?.state || !ValidFileState.includes(formData.titleImageUrl?.state)) {
    errors.push(`Story title image is missing!`);
  }

  if (errors.length > 0) {
    return {
      errors: errors,
    };
  }

  const storyDocRef = !id ? doc(collection(firestore, 'stories')) : doc(firestore, `stories/${id}`);

  const timestamp = serverTimestamp() as Timestamp;

  const parsedStoryData: Partial<Story> = {
    title: formData.title,
    storyAbout: formData.storyAbout,
    storyEnd: formData.storyEnd,
    publishDate: formData?.avilableDateRange?.start
      ? Timestamp.fromDate(formData?.avilableDateRange.start)
      : null,
    expirationDate: formData?.avilableDateRange?.end
      ? Timestamp.fromDate(formData?.avilableDateRange.end)
      : null,
    entryFee: { coins: formData.enteryFee },
    reward: { coins: formData.storyReward },
    createdAt: timestamp,
    publisher: formData.publisher.value,
  };

  const storyStatus: Story['status'] = getStoryStatus(
    publish,
    !!formData?.avilableDateRange?.start,
  );

  if (storyStatus) {
    parsedStoryData.status = storyStatus;
    if (storyStatus === 'published') {
      parsedStoryData.publishedAt = timestamp;
    }
  }

  const storyImageRef = ref(storage, `stories/${storyDocRef.id}/image`);
  const storySoundtrackRef = ref(storage, `stories/${storyDocRef.id}/soundtrack`);
  const storyTitleImageRef = ref(storage, `stories/${storyDocRef.id}/titleImage`);

  // Upload story image
  if (files.image) {
    const resizeResult = await files?.image.arrayBuffer(); // TODO Switch to this once size is decided await resizeAndGetBuffer(files?.image, xxxx, xxxx, files.image.type), TASK_ID: #8694x2n39
    const uploadResults = await uploadBytes(storyImageRef, resizeResult, {
      contentType: files.image.type,
    });
    parsedStoryData.imageUrl = `${STORAGE_URL}/${
      uploadResults.ref.fullPath
    }?timestamp=${new Date().getTime()}`;
  }

  // Upload story soundtrack
  if (files.soundtrack) {
    const uploadResults = await uploadBytes(
      storySoundtrackRef,
      await files.soundtrack.arrayBuffer(),
      {
        contentType: files.soundtrack.type,
      },
    );
    parsedStoryData.soundtrackUrl = `${STORAGE_URL}/${
      uploadResults.ref.fullPath
    }?timestamp=${new Date().getTime()}`;
  } else {
    if (id) {
      parsedStoryData.soundtrackUrl = null;
      try {
        await deleteObject(storySoundtrackRef);
      } catch (err) {
        console.log(err);
      }
    }
  }

  // Upload title image
  if (files.titleImage) {
    const resizeResult = await files.titleImage.arrayBuffer(); // TODO Switch to this once size is decided await resizeAndGetBuffer(files?.image, xxxx, xxxx, ImageFormat.PNG), TASK_ID: #8694x2n39
    const uploadResults = await uploadBytes(storyTitleImageRef, resizeResult, {
      contentType: files.titleImage.type,
    });
    parsedStoryData.titleImageUrl = `${STORAGE_URL}/${
      uploadResults.ref.fullPath
    }?timestamp=${new Date().getTime()}`;
  }

  return {
    docRef: storyDocRef,
    data: parsedStoryData,
  };
};
